import axios from 'axios';
import { API_BASE_URL } from './apiConfig';
import { hasPermission } from './permissions';

// All portal sections with display labels (must match backend PORTAL_SECTIONS)
export const PORTAL_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'students', label: 'Students' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'courses', label: 'Courses' },
  { id: 'applications', label: 'Applications' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'finance', label: 'Finance' },
  { id: 'staff', label: 'Staff Management' },
  { id: 'profile', label: 'Settings' }
];

// Default path per section (for redirect when user has no dashboard access)
export const SECTION_DEFAULT_PATHS = {
  dashboard: '/dashboard',
  students: '/students/list',
  sessions: '/sessions',
  courses: '/courses',
  applications: '/applications/all-applied-candidates',
  attendance: '/attendance/add',
  finance: '/finance/billing',
  staff: '/staff/all',
  profile: '/profile/academic-year'
};

/** Privileges not tied to PORTAL_SECTIONS CRUD (must match backend VALID_PRIVILEGES where applicable) */
export const STANDALONE_PRIVILEGES = [
  { key: 'activity_log_read', label: 'Activity log – View', defaultPath: '/activity-log' }
];

const STANDALONE_PRIVILEGE_KEYS = new Set(STANDALONE_PRIVILEGES.map((s) => s.key));

/** First route the user is allowed to access (by section read permission), or /dashboard for admin */
export function getFirstAllowedPath(user) {
  if (!user) return '/dashboard';
  if (user.role === 'admin') return '/dashboard';
  for (const { id } of PORTAL_SECTIONS) {
    const path = SECTION_DEFAULT_PATHS[id];
    if (path && hasPermission(user, getPrivilegeKey(id, 'read'))) return path;
  }
  for (const { key, defaultPath } of STANDALONE_PRIVILEGES) {
    if (defaultPath && hasPermission(user, key)) return defaultPath;
  }
  return '/dashboard';
}

const CRUD = ['create', 'read', 'update', 'delete'];

// Privilege key for a section and action (e.g. applications_read)
export const getPrivilegeKey = (sectionId, action) => `${sectionId}_${action}`;

// All four privilege keys for a section
export const getSectionPrivileges = (sectionId) =>
  CRUD.map((action) => getPrivilegeKey(sectionId, action));

// Human-readable label for an action
export const CRUD_LABELS = { create: 'Create', read: 'Read', update: 'Update', delete: 'Delete' };

// All valid privilege keys (for validation / display)
export const ALL_PRIVILEGES = PORTAL_SECTIONS.flatMap((s) =>
  getSectionPrivileges(s.id)
);

// Legacy labels for display in tables (old or unknown keys)
export const PRIVILEGE_LABELS = {
  ...Object.fromEntries(
    PORTAL_SECTIONS.flatMap((sec) =>
      CRUD.map((action) => [
        getPrivilegeKey(sec.id, action),
        `${sec.label} – ${CRUD_LABELS[action]}`
      ])
    )
  ),
  sessions_view: 'Sessions – Read',
  sessions_add: 'Sessions – Create',
  activity_log_read: 'Activity log – View',
  staff_read: 'Staff Management – Read'
};

// Section ID to label (for grouped display)
const sectionIdToLabel = Object.fromEntries(PORTAL_SECTIONS.map((s) => [s.id, s.label]));

// Display order for actions when grouping
const ACTION_ORDER = ['read', 'create', 'update', 'delete'];

/** Format privileges for table display: "Applications: Create, Read, Update, Attendance: Create, Read" */
export function formatPrivilegesGroupedBySection(privileges) {
  if (!privileges || privileges.length === 0) return '';
  const bySection = {};
  privileges.forEach((priv) => {
    if (STANDALONE_PRIVILEGE_KEYS.has(priv)) return;
    let sectionId, action;
    if (priv === 'sessions_view' || priv === 'sessions_add') {
      sectionId = 'sessions';
      action = priv === 'sessions_view' ? 'read' : 'create';
    } else {
      const lastUnderscore = priv.lastIndexOf('_');
      if (lastUnderscore === -1) return;
      sectionId = priv.slice(0, lastUnderscore);
      action = priv.slice(lastUnderscore + 1);
    }
    if (!bySection[sectionId]) bySection[sectionId] = [];
    if (!bySection[sectionId].includes(action)) bySection[sectionId].push(action);
  });
  const sectionOrder = PORTAL_SECTIONS.map((s) => s.id);
  const mainPart = sectionOrder
    .filter((id) => bySection[id]?.length)
    .map((sectionId) => {
      const label = sectionIdToLabel[sectionId] || sectionId;
      const actions = (bySection[sectionId] || []).sort(
        (a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b)
      );
      const actionLabels = actions.map((a) => CRUD_LABELS[a] || a).join(', ');
      return `${label}: ${actionLabels}`;
    })
    .join(', ');
  const standalonePart = STANDALONE_PRIVILEGES.filter((s) => privileges.includes(s.key))
    .map((s) => s.label)
    .join(', ');
  return [mainPart, standalonePart].filter(Boolean).join(', ');
}

export async function fetchPositions() {
  const response = await axios.get(`${API_BASE_URL}/positions`);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch positions');
  return response.data.positions;
}

export async function createPosition({ name, description, privileges }) {
  const response = await axios.post(`${API_BASE_URL}/positions`, { name, description, privileges });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to create position');
  return response.data;
}

export async function updatePosition(id, { name, description, privileges }) {
  const response = await axios.put(`${API_BASE_URL}/positions/${id}`, { name, description, privileges });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to update position');
  return response.data;
}

export async function deletePosition(id) {
  const response = await axios.delete(`${API_BASE_URL}/positions/${id}`);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to delete position');
  return response.data;
}
